import * as React from 'react'
import { useState } from 'react'
import { makeStyles, mergeClasses, Text, tokens } from '@fluentui/react-components'
import { ChevronDownRegular, ChevronRightRegular, PinColor, PinRegular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import useAxios from '../../../hooks/useAxios'
import PersonAvatar from '../../../components/PersonAvatar'
import FormattedDate from '../../../components/Date/FormattedDate'
import { DateFormats } from '../../../helpers/dateHelper'
import { RichText } from '../../components/Text/RichText'
import TooltippedButton from '../../components/Button/TooltippedButton'
import TooltippedText from '../../components/Text/TooltippedText'
import { useOverviewStyles } from '../../../pages/BaseOverview/BaseOverview'
import { BaseNoteLineOverviewProps } from './BaseNoteOverview'

const useStyles = makeStyles({
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
        minWidth: 0,
    },
    // One line: a title of a hundred characters may not push the note open. The whole title is in the
    // tooltip, so nothing is lost by cutting it off.
    title: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    meta: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
        color: tokens.colorNeutralForeground3,
    },
    body: {
        // Both cells of the row, so the description runs the full width of the note.
        gridColumn: '1 / -1',
        overflowWrap: 'anywhere',
    },
    // Collapsed shows the first few lines with the formatting intact. A fixed height would be wrong here:
    // BaseOverview reserves 800px for a page that has not loaded yet, so a note must not grow without bound.
    bodyCollapsed: {
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    iconSize: {
        maxHeight: '24px',
    },
})

/**
 * A note in the list (HAM-3476). Collapsed it shows the title, the author, the creation date and the first
 * lines of the description; the chevron expands it to the whole description. Expanding is reading - clicking
 * the row itself opens the edit dialog, and BaseMutableOverview always wires that up - so both buttons stop
 * the click from bubbling.
 */
export default function NoteListItem(props: BaseNoteLineOverviewProps) {
    const axios = useAxios()
    const styles = useStyles()
    const overviewStyles = useOverviewStyles()
    const { t: translate } = useTranslation()

    const [expanded, setExpanded] = useState<boolean>(false)
    const [pinned, setPinned] = useState<boolean>(props.item.pinned === true)

    const togglePinned = () =>
        axios.post(`/note/${props.item.id}/pin`, `${!pinned}`, { headers: { 'Content-Type': 'text/plain' } }).then(() => {
            setPinned(!pinned)
            // Pinning changes the order across pages, so the list has to be fetched again; updating the
            // single item would leave the note sitting in its old spot.
            props.reloadItems?.()
        })

    return (
        <>
            <div className={styles.header}>
                <TooltippedText weight={'semibold'} className={styles.title} tooltip={props.item.title}>
                    {props.item.title}
                </TooltippedText>
                <div className={styles.meta}>
                    <PersonAvatar name={props.item.createdByAuthor?.fullName} color={'colorful'} size={24} />
                    <Text size={200}>
                        <FormattedDate date={props.item.createdAt} format={DateFormats.Full} />
                    </Text>
                </div>
            </div>
            <div className={overviewStyles.flexGapXS}>
                {/* The pin only appears on hover, but a pinned note keeps showing it - otherwise the marking would
                    exist only while hovering (AC 4). It sits left of the chevron so that chevron never moves. */}
                <aside data-hover-actions={pinned ? 'true' : 'display'}>
                    <TooltippedButton
                        size={'small'}
                        appearance={'subtle'}
                        display={'iconOnly'}
                        label={translate(pinned ? 'file.pivot.note.action.unpin' : 'file.pivot.note.action.pin')}
                        icon={pinned ? <PinColor /> : <PinRegular />}
                        onClick={event => {
                            event.stopPropagation()
                            togglePinned()
                        }}
                        className={styles.iconSize}
                    />
                </aside>
                {props.item.description && (
                    <TooltippedButton
                        size={'small'}
                        appearance={'subtle'}
                        display={'iconOnly'}
                        label={translate(expanded ? 'file.pivot.note.action.collapse' : 'file.pivot.note.action.expand')}
                        icon={expanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
                        onClick={event => {
                            event.stopPropagation()
                            setExpanded(!expanded)
                        }}
                        className={styles.iconSize}
                    />
                )}
            </div>
            {props.item.description && (
                <RichText allowLinks content={props.item.description} className={mergeClasses(styles.body, !expanded && styles.bodyCollapsed)} />
            )}
        </>
    )
}
